import type { BaseAPIResponse } from '~/types/api';
import type { AuthUser } from '~/types/auth/auth';
import { ApiError } from '~/utils/errors';

// Pure API service - no state management
export const authService = {
    async getMe(): Promise<AuthUser> {
        const nuxtApp = useNuxtApp();

            const { data, status, message } = await usePbApi<BaseAPIResponse<AuthUser>>(nuxtApp.$api.routes.me.getMe());

            if (status !== 200) {
                const statusCode = status || 500;
                throw new ApiError(message || 'Error while fetching user info', statusCode);
            }

            if (!data?.data) {
                throw new ApiError('No user data received', 404);
            }

            return data.data;
    },

    async startEmailAuth(email: string): Promise<{ step: string; authenticator: boolean }> {
        const config = useRuntimeConfig();
        const nuxtApp = useNuxtApp();
        const res = await $fetch<{ data: { step: string; authenticator: boolean; pin?: boolean; email?: string } }>(
            nuxtApp.$api.routes.auth.start(),
            {
                baseURL: config.public.pb.apiDomain,
                method: 'POST',
                credentials: 'include',
                headers: { Accept: 'application/json' },
                body: { email, verification_method: 'pin' },
            },
        );
        return { step: res.data.step, authenticator: !!res.data.authenticator };
    },

    async verifyEmailAuth(payload: { email: string; pin: string; authenticator?: string }): Promise<
        | { ok: true; data: { step: string; token: string; refresh_token: string; profile_ready?: boolean } }
        | { ok: false; reason?: string; message?: string }
    > {
        const config = useRuntimeConfig();
        const nuxtApp = useNuxtApp();
        try {
            const res = await $fetch<{ data: { step: string; token: string; refresh_token: string; profile_ready?: boolean } }>(
                nuxtApp.$api.routes.auth.verify(),
                {
                    baseURL: config.public.pb.apiDomain,
                    method: 'POST',
                    credentials: 'include',
                    headers: { Accept: 'application/json' },
                    body: { email: payload.email, pin: payload.pin, authenticator: payload.authenticator ?? '' },
                },
            );
            return { ok: true, data: res.data };
        } catch (err: any) {
            const body = err?.data ?? err?.response?._data;
            return { ok: false, reason: body?.data?.reason ?? body?.reason, message: body?.message };
        }
    },

    async updateProfileName(payload: { first_name: string; last_name: string }): Promise<void> {
        const nuxtApp = useNuxtApp();
        const { status, message } = await usePbApi(nuxtApp.$api.routes.auth.profile(), {
            method: 'PATCH',
            body: JSON.stringify({ first_name: payload.first_name, last_name: payload.last_name }),
        });
        if (status !== 200) {
            throw new ApiError(message || 'Failed to save your name', status || 500);
        }
    },
};